/**
 * chartAxis — the tick arithmetic the panel's two-axis charts share.
 *
 * The project activity chart and the feed's hour-of-day profile both draw two
 * series whose volumes differ by orders of magnitude against two axes, and both
 * want the same thing from an axis: a round number a reader can halve in their
 * head. The rounding lives here rather than in either chart so the two cannot
 * drift into labelling the same kind of scale two different ways, and so a fix
 * to the rounding is a fix to both.
 *
 * It is deliberately only arithmetic. Which ticks exist, how they are drawn and
 * what colour they carry are decisions each chart makes for itself.
 */

/** niceCeiling rounds up to 1/2/5 × a power of ten, so an axis reads 20 rather than 17. */
export function niceCeiling(value) {
  const magnitude = 10 ** Math.floor(Math.log10(value));
  for (const step of [1, 2, 5, 10]) {
    if (value <= step * magnitude) return step * magnitude;
  }
  return 10 * magnitude;
}

/**
 * axisTicks is the ticks a chart of `max` needs: the floor, the middle and the
 * rounded top.
 *
 * Half is a stop of its own rather than an interpolation the reader does: a
 * two-axis chart is read by comparing a bar with a printed number, and "half of
 * the top" is the comparison people actually make. A window with nothing in it
 * still returns the floor, so an empty chart draws a baseline rather than a
 * division by zero.
 */
export function axisTicks(max) {
  if (max <= 0) return [0];
  const top = niceCeiling(max);
  return [0, top / 2, top];
}
